import { useState, useEffect, useMemo, useRef } from 'react';
import CompareBarChart from './CompareBarChart';
import TrendChart from '../charts/TrendChart';
import { getAllocationData, getAllocationTrend, getCohorts } from '../../utils/api';
import { useColorScheme } from '../../hooks/useColorScheme';
import { COLORS, COMPARE_RAMP, partyColor } from '../../utils/colors';
import { downloadCsv } from '../../utils/exportCsv';

/**
 * Compare view — the default tab. Compares question results across the
 * segments of one demographic/custom dimension.
 *
 * Dimensions, labels, and segment values all come from the same /config
 * payload the classic board uses (dashboard_filter_config +
 * filter_value_config + custom_filter_config) — one source of truth for
 * both tabs. Per-segment data comes from the existing endpoints with a
 * single filter applied; grouped display values expand through the same
 * filterValueMappings mechanism as the filter bar.
 *
 * No sample sizes are shown or exported anywhere in this view.
 */

const ORDINAL_DIMS = new Set(['Age', 'Education', 'Income']);
const MAX_STATES = 6;

export default function CompareView({
  clientKey, questions, selectedQuestionId, onSelectQuestion,
  dimensions, filterLabels, filterValueMappings
}) {
  const { palette, colors: schemeColors } = useColorScheme();

  const question = questions.find(q => q.questionId === selectedQuestionId) || questions[0];
  const flags = question?.chartFlags || {};
  const qType = question?.questionType;

  // Dimensions offered = visible filters from config. State only on baseline.
  const dimNames = useMemo(() => {
    const names = Object.keys(dimensions || {}).filter(name => {
      if (name === 'State') return clientKey === 'baseline';
      return (dimensions[name] || []).length >= 2;
    });
    return ['Off', ...names];   // 'Off' = no comparison (default)
  }, [dimensions, clientKey]);

  const [dim, setDim] = useState(null);
  const [view, setView] = useState('Chart');
  const [selOption, setSelOption] = useState(null);
  const [stateSel, setStateSel] = useState([]);
  const [segData, setSegData] = useState({});      // segment -> { alloc, trend, ci, ri }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const prevKeyRef = useRef('');

  const activeDim = dim && dimNames.includes(dim) ? dim : dimNames[0];
  const isOff = activeDim === 'Off';

  // State segment selection (baseline): default to first 6 visible states
  const stateValues = dimensions?.State || [];
  const activeStates = stateSel.length > 0 ? stateSel : stateValues.slice(0, MAX_STATES);

  const segments = useMemo(() => {
    if (!activeDim) return [];
    if (isOff) return ['All'];
    if (activeDim === 'State') return activeStates;
    return dimensions[activeDim] || [];
  }, [activeDim, isOff, dimensions, activeStates.join('|')]);

  // ── Fetch per-segment data ────────────────────────────────────────────────
  useEffect(() => {
    if (!question || !clientKey || !activeDim || segments.length === 0) return;

    const fetchKey = [clientKey, question.questionId, activeDim, segments.join('|')].join('::');
    if (fetchKey === prevKeyRef.current) return;
    prevKeyRef.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const wantTrend = (qType === 'allocation' || qType === 'slider') && flags.showAllocTrend !== false;
    const wantCohorts = qType === 'allocation' && flags.showCIRI !== false;

    async function fetchSegment(seg) {
      const segFilter = isOff ? {} : { [activeDim]: [seg] };
      const jobs = {
        alloc: getAllocationData(clientKey, question.questionId, segFilter, filterValueMappings)
      };
      if (wantTrend) {
        jobs.trend = getAllocationTrend(clientKey, question.questionId, segFilter,
          { trendStartDate: question.trendStartDate, valueMappings: filterValueMappings });
      }
      if (wantCohorts) {
        jobs.ci = getCohorts(clientKey, question.questionId, 'commitment', segFilter, filterValueMappings);
        jobs.ri = getCohorts(clientKey, question.questionId, 'rejection', segFilter, filterValueMappings);
      }
      const keys = Object.keys(jobs);
      const results = await Promise.allSettled(keys.map(k => jobs[k]));
      const out = {};
      keys.forEach((k, i) => { out[k] = results[i].status === 'fulfilled' ? results[i].value : null; });
      return out;
    }

    Promise.allSettled(segments.map(fetchSegment)).then(results => {
      if (cancelled) return;
      const next = {};
      let anyOk = false;
      segments.forEach((seg, i) => {
        if (results[i].status === 'fulfilled') { next[seg] = results[i].value; anyOk = true; }
      });
      if (!anyOk) setError('Failed to load comparison data.');
      setSegData(next);
      setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientKey, question?.questionId, activeDim, segments.join('|')]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const segAlloc = (seg) => {
    const a = segData[seg]?.alloc;
    if (!a) return null;
    return (a.filtered && a.filtered.length > 0) ? a.filtered : a.overall;
  };

  const optionNames = useMemo(() => {
    for (const seg of segments) {
      const rows = segAlloc(seg);
      if (rows && rows.length > 0) return rows.map(r => r.optionText);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segData, segments]);

  const activeOption = selOption && optionNames.includes(selOption) ? selOption : optionNames[0];

  const segColors = useMemo(() => {
    if (isOff) return [palette[0]];
    if (activeDim === 'Party') {
      return segments.map((s, i) => partyColor(s) || palette[i % palette.length]);
    }
    if (ORDINAL_DIMS.has(activeDim)) {
      const k = segments.length, R = COMPARE_RAMP;
      if (k <= 1) return [R[2]];
      if (k === 2) return [R[1], R[4]];
      if (k === 3) return [R[0], R[2], R[4]];
      if (k === 4) return [R[0], R[1], R[3], R[4]];
      return segments.map((_, i) => R[Math.min(R.length - 1, i)]);
    }
    return segments.map((_, i) => palette[i % palette.length]);
  }, [activeDim, segments, palette]);

  const valueFor = (seg, opt, kind) => {
    if (kind === 'alloc') {
      const rows = segAlloc(seg);
      const row = rows?.find(r => r.optionText === opt);
      return row ? round1(row.avgValue) : null;
    }
    if (kind === 'pctRespondents') {
      const rows = segAlloc(seg);
      if (!rows) return null;
      const total = rows.reduce((s, r) => s + (r.responseCount || 0), 0);
      const row = rows.find(r => r.optionText === opt);
      return total > 0 && row ? round1((row.responseCount || 0) / total * 100) : null;
    }
    // ci / ri
    const resp = segData[seg]?.[kind];
    if (!resp) return null;
    const source = resp.filtered && Object.keys(resp.filtered).length > 0 ? resp.filtered : resp.overall;
    return extractCohortValue(source?.[opt], kind === 'ri' ? 'rejection' : 'commitment');
  };

  const barSeries = (kind) => {
    if (isOff) {
      const color = kind === 'ci' ? schemeColors.teal
        : kind === 'ri' ? schemeColors.coral
        : optionNames.map((_, i) => palette[i % palette.length]);
      return [{
        name: kind === 'ci' ? 'Commitment Index' : kind === 'ri' ? 'Rejection Index' : 'Average',
        color,
        data: optionNames.map(opt => valueFor('All', opt, kind))
      }];
    }
    return segments.map((seg, i) => ({
      name: seg,
      color: segColors[i],
      data: optionNames.map(opt => valueFor(seg, opt, kind))
    }));
  };

  const trendSeries = useMemo(() => {
    if (isOff) {
      const t = segData['All']?.trend;
      if (!t?.dates || !t.series || t.series.length === 0) return null;
      return {
        dates: t.dates,
        series: t.series.map(s => ({ name: s.name, data: s.data })),
        colors: t.series.map((_, i) => palette[i % palette.length])
      };
    }
    const singleOption = optionNames.length <= 1;   // sliders etc. — no option matching needed
    if (!singleOption && !activeOption) return null;
    let dates = null;
    const series = [];
    segments.forEach((seg, i) => {
      const t = segData[seg]?.trend;
      if (!t?.dates || !t.series) return;
      const s = singleOption
        ? (t.series[0] || null)
        : (t.series.find(x => x.name === activeOption)
            || (t.series.length === 1 ? t.series[0] : null));  // fallback: series name differs from allocation optionText
      if (!s) return;
      if (!dates || t.dates.length > dates.length) dates = t.dates;
      series.push({ name: seg, data: s.data, _dates: t.dates, _color: segColors[i] });
    });
    if (!dates || series.length === 0) return null;
    // Align each segment's series onto the longest date axis
    const aligned = series.map(s => {
      const byDate = {};
      s._dates.forEach((d, i) => { byDate[d] = s.data[i]; });
      return { name: s.name, data: dates.map(d => byDate[d] ?? null), _color: s._color };
    });
    return { dates, series: aligned, colors: aligned.map(s => s._color) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segData, segments, activeOption, segColors]);

  // ── CSV builders (chart values only — never sample sizes) ────────────────
  const dimLabel = isOff ? null : (filterLabels?.[activeDim] || activeDim);
  const qName = question?.questionName || 'Question';

  const barCsv = (kind, label) => () => {
    const pctCol = label === '% of Respondents' || kind === 'ci' || kind === 'ri';
    if (isOff) {
      return downloadCsv(
        `${qName} - ${label}`,
        ['Option', pctCol ? `${label} (%)` : label],
        optionNames.map(opt => [opt, fmt(valueFor('All', opt, kind))])
      );
    }
    downloadCsv(
      `${qName} - ${label} by ${dimLabel}`,
      [dimLabel, ...optionNames.map(o => pctCol ? `${o} (%)` : o)],
      segments.map((seg, i) => [seg, ...optionNames.map(opt => fmt(valueFor(seg, opt, kind)))])
    );
  };
  const trendCsv = () => {
    if (!trendSeries) return;
    downloadCsv(
      isOff || optionNames.length <= 1
        ? (isOff ? `${qName} - Trendline` : `${qName} - Trendline by ${dimLabel}`)
        : `${qName} - Trendline ${activeOption} by ${dimLabel}`,
      ['Month', ...trendSeries.series.map(s => s.name)],
      trendSeries.dates.map((d, i) => [d, ...trendSeries.series.map(s => fmt(s.data[i]))])
    );
  };

  if (!question) {
    return <div style={emptyStyle}>No questions available for this dashboard.</div>;
  }

  const showTrend = (qType === 'allocation' || qType === 'slider') && flags.showAllocTrend !== false;
  const showCohorts = qType === 'allocation' && flags.showCIRI !== false;
  const isChoice = qType === 'single_choice' || qType === 'scale_rating';
  const barKind = isChoice ? 'pctRespondents' : 'alloc';
  const barLabel = isChoice ? '% of Respondents' : 'Average Allocation';

  const segCount = isOff ? 1 : segments.length;
  const barHeight = Math.max(180, optionNames.length * (segCount * 22 + 26) + 40);
  const cohortHeight = Math.max(160, optionNames.length * (segCount * 18 + 24) + 40);

  return (
    <div>
      {/* Controls */}
      <div style={controlsStyle}>
        <div style={ctlGroupStyle}>
          <span style={ctlLabelStyle}>QUESTION</span>
          <select
            style={{ ...selectStyle, maxWidth: '320px' }}
            value={question.questionId}
            onChange={(e) => onSelectQuestion(e.target.value)}
          >
            {questions.map(q => (
              <option key={q.questionId} value={q.questionId}>{q.questionName}</option>
            ))}
          </select>
        </div>

        <div style={ctlGroupStyle}>
          <span style={ctlLabelStyle}>COMPARE BY</span>
          <div style={segWrapStyle} role="group" aria-label="Compare by">
            {dimNames.map(name => (
              <button
                key={name}
                type="button"
                aria-pressed={name === activeDim}
                onClick={() => setDim(name)}
                style={{ ...segBtnStyle, ...(name === activeDim ? segBtnActiveStyle : {}) }}
              >
                {filterLabels?.[name] || name}
              </button>
            ))}
          </div>
        </div>

        {showTrend && !isOff && optionNames.length > 1 && (
          <div style={ctlGroupStyle}>
            <span style={ctlLabelStyle}>OPTION</span>
            <select
              style={selectStyle}
              value={activeOption || ''}
              onChange={(e) => setSelOption(e.target.value)}
              aria-label="Question option (trendline)"
            >
              {optionNames.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}

        <div style={{ ...ctlGroupStyle, marginLeft: 'auto' }}>
          <span style={ctlLabelStyle}>VIEW</span>
          <div style={segWrapStyle} role="group" aria-label="View">
            {['Chart', 'Table'].map(v => (
              <button
                key={v}
                type="button"
                aria-pressed={v === view}
                onClick={() => setView(v)}
                style={{ ...segBtnStyle, ...(v === view ? segBtnActiveStyle : {}) }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* State segment picker (baseline only) */}
      {activeDim === 'State' && (
        <div style={statePickerStyle}>
          <span style={ctlLabelStyle}>STATES (MAX {MAX_STATES})</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {stateValues.map(st => {
              const on = activeStates.includes(st);
              return (
                <button
                  key={st}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    const cur = activeStates.slice();
                    const i = cur.indexOf(st);
                    if (i >= 0) { if (cur.length > 1) cur.splice(i, 1); }
                    else if (cur.length < MAX_STATES) cur.push(st);
                    setStateSel(cur);
                  }}
                  style={{ ...stateChipStyle, ...(on ? stateChipActiveStyle : {}) }}
                >
                  {st}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Question header panel */}
      <div style={qPanelStyle}>
        <div>
          <div style={qLabelStyle}>Question</div>
          <div style={qNameStyle}>{question.questionName}</div>
        </div>
        <div>
          <div style={qLabelStyle}>Question Text</div>
          <p style={qDescStyle}>{question.description || ''}</p>
        </div>
      </div>

      {error && <div style={{ ...emptyStyle, color: COLORS.coral }}>{error}</div>}

      {/* Charts — held at reduced opacity during refetch */}
      <div style={{ opacity: loading ? 0.45 : 1, transition: 'opacity 0.2s' }}>

        <Card
          title={barLabel}
          note={isOff ? null : `by ${dimLabel}`}
          onCsv={optionNames.length > 0 ? barCsv(barKind, barLabel) : null}
        >
          {optionNames.length === 0 ? (
            <div style={emptyStyle}>{loading ? 'Loading comparison data…' : 'No data available.'}</div>
          ) : view === 'Table' ? (
            <CompareTable
              firstCol={isOff ? 'Option' : dimLabel}
              headers={isOff ? [barLabel] : optionNames}
              rows={isOff
                ? optionNames.map((opt, i) => ({
                    label: opt, color: palette[i % palette.length],
                    values: [fmt(valueFor('All', opt, barKind))]
                  }))
                : segments.map((seg, i) => ({
                    label: seg, color: segColors[i],
                    values: optionNames.map(opt => fmt(valueFor(seg, opt, barKind)))
                  }))}
            />
          ) : (
            <>
              {isOff
                ? <Legend segments={optionNames} colors={optionNames.map((_, i) => palette[i % palette.length])} />
                : <Legend segments={segments} colors={segColors} />}
              <div style={{ height: barHeight + 'px' }}>
                <CompareBarChart options={optionNames} series={barSeries(barKind)} pct={isChoice} />
              </div>
            </>
          )}
        </Card>

        {showTrend && (
          <Card
            title={
              isOff
                ? (qType === 'slider' ? 'Average Trendline' : 'Allocation Trendline')
                : optionNames.length <= 1
                  ? `${qType === 'slider' ? 'Average' : 'Allocation'} Trendline by ${dimLabel}`
                  : `Allocation Trendline — ${activeOption || ''} by ${dimLabel}`
            }
            onCsv={trendSeries ? trendCsv : null}
          >
            {!trendSeries ? (
              <div style={emptyStyle}>{loading ? 'Loading…' : 'No trend data available.'}</div>
            ) : view === 'Table' ? (
              <CompareTable
                firstCol="Month"
                headers={trendSeries.series.map(s => s.name)}
                rows={trendSeries.dates.map((d, i) => ({
                  label: formatMonth(d),
                  values: trendSeries.series.map(s => fmt(s.data[i]))
                }))}
              />
            ) : (
              <>
                <Legend segments={trendSeries.series.map(s => s.name)} colors={trendSeries.colors} line />
                <div style={{ height: '260px' }}>
                  <TrendChart data={trendSeries} colors={trendSeries.colors} />
                </div>
              </>
            )}
          </Card>
        )}

        {showCohorts && (
          <div style={halfGridStyle}>
            {[['ci', 'Commitment Index'], ['ri', 'Rejection Index']].map(([kind, title]) => (
              <Card key={kind} title={title} note={isOff ? null : `by ${dimLabel}`}
                onCsv={optionNames.length > 0 ? barCsv(kind, title) : null}>
                {optionNames.length === 0 ? (
                  <div style={emptyStyle}>{loading ? 'Loading…' : 'No data available.'}</div>
                ) : view === 'Table' ? (
                  <CompareTable
                    firstCol={isOff ? 'Option' : dimLabel}
                    headers={isOff ? [title + ' (%)'] : optionNames.map(o => o + ' (%)')}
                    rows={isOff
                      ? optionNames.map(opt => ({
                          label: opt,
                          values: [fmt(valueFor('All', opt, kind))]
                        }))
                      : segments.map((seg, i) => ({
                          label: seg, color: segColors[i],
                          values: optionNames.map(opt => fmt(valueFor(seg, opt, kind)))
                        }))}
                  />
                ) : (
                  <>
                    {!isOff && <Legend segments={segments} colors={segColors} />}
                    <div style={{ height: cohortHeight + 'px' }}>
                      <CompareBarChart options={optionNames} series={barSeries(kind)} pct xMax={100} />
                    </div>
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

const round1 = (v) => (v == null || isNaN(v) ? null : Math.round(v * 10) / 10);
const fmt = (v) => (v == null ? '' : v.toFixed(1));

function extractCohortValue(cohorts, type) {
  if (cohorts == null) return null;
  if (typeof cohorts === 'number') return round1(cohorts);
  const tryKeys = type === 'rejection'
    ? ['Rejected', 'rejected', 'Rejection', 'rejection', 'RI', 'ri']
    : ['Committed', 'committed', 'Commitment', 'commitment', 'CI', 'ci'];
  for (const k of tryKeys) { if (cohorts[k] != null) return round1(cohorts[k]); }
  const vals = Object.values(cohorts).filter(v => typeof v === 'number');
  return vals.length > 0 ? round1(Math.max(...vals)) : null;
}

function formatMonth(dateStr) {
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function Card({ title, note, onCsv, children }) {
  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div style={cardAccentStyle}></div>
        <div style={cardTitleStyle}>{title}</div>
        {note && <div style={cardNoteStyle}>· {note}</div>}
        {onCsv && (
          <button style={csvBtnStyle} onClick={onCsv} title="Download chart values as CSV">
            CSV &#10515;
          </button>
        )}
      </div>
      <div style={cardBodyStyle}>{children}</div>
    </div>
  );
}

function Legend({ segments, colors, line = false }) {
  return (
    <div style={legendStyle}>
      {segments.map((s, i) => (
        <div key={s} style={legendItemStyle} title={s}>
          <div style={{
            background: colors[i],
            ...(line ? { width: '16px', height: '3px', borderRadius: '2px', marginTop: '7px' }
                     : { width: '12px', height: '12px', borderRadius: '3px', marginTop: '2px' })
          }}></div>
          <span style={legendTextStyle}>{s}</span>
        </div>
      ))}
    </div>
  );
}

function CompareTable({ firstCol, headers, rows }) {
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle}>{firstCol}</th>
          {headers.map(h => <th key={h} style={{ ...thStyle, textAlign: 'right' }}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>
            <td style={tdStyle}>
              {r.color && <span style={{ ...swatchStyle, background: r.color }} />}
              {r.label}
            </td>
            {r.values.map((v, vi) => <td key={vi} style={tdNumStyle}>{v}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const controlsStyle = {
  background: '#fff', borderRadius: '14px', padding: '14px 20px',
  border: '1px solid #e2e6ea', marginBottom: '12px',
  display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap'
};

const ctlGroupStyle = { display: 'flex', alignItems: 'center', gap: '9px' };

const ctlLabelStyle = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px',
  color: '#8a94a3', textTransform: 'uppercase'
};

const segWrapStyle = {
  display: 'inline-flex', background: '#f7f8fa', border: '1px solid #e2e6ea',
  borderRadius: '9px', padding: '3px', flexWrap: 'wrap'
};

const segBtnStyle = {
  border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: '12.5px', fontWeight: 600, color: '#5a6577', padding: '6px 11px', borderRadius: '7px'
};

const segBtnActiveStyle = { background: '#0f4c5c', color: '#fff' };

const selectStyle = {
  fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, color: '#1a2332',
  background: '#f7f8fa', border: '1px solid #e2e6ea', borderRadius: '9px',
  padding: '8px 10px', cursor: 'pointer'
};

const statePickerStyle = {
  background: '#fff', borderRadius: '14px', padding: '12px 20px',
  border: '1px solid #e2e6ea', marginBottom: '12px',
  display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap'
};

const stateChipStyle = {
  border: '1px solid #e2e6ea', background: '#fff', borderRadius: '99px',
  padding: '5px 12px', fontSize: '12px', fontWeight: 600, color: '#5a6577',
  cursor: 'pointer', fontFamily: 'inherit'
};

const stateChipActiveStyle = { background: '#0f4c5c', borderColor: '#0f4c5c', color: '#fff' };

const qPanelStyle = {
  background: '#fff', borderRadius: '14px', padding: '24px',
  border: '1px solid #e2e6ea', marginBottom: '20px',
  display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px'
};

const qLabelStyle = {
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
  color: '#8a94a3', marginBottom: '8px'
};

const qNameStyle = { fontSize: '14px', fontWeight: 600, color: '#1a2332' };
const qDescStyle = { fontSize: '13px', lineHeight: 1.7, color: '#5a6577' };

const cardStyle = {
  background: '#fff', borderRadius: '14px',
  border: '1px solid #e2e6ea', overflow: 'hidden', marginBottom: '20px'
};

const cardHeaderStyle = {
  padding: '14px 20px', borderBottom: '1px solid #e2e6ea',
  display: 'flex', alignItems: 'center', gap: '10px'
};

const cardAccentStyle = {
  width: '4px', height: '18px', borderRadius: '2px',
  background: 'linear-gradient(180deg, #2ec4b6, #0f4c5c)'
};

const cardTitleStyle = { fontSize: '14px', fontWeight: 600, color: '#1a2332' };
const cardNoteStyle = { fontSize: '12px', color: '#8a94a3' };
const cardBodyStyle = { padding: '20px' };

const csvBtnStyle = {
  marginLeft: 'auto', background: 'transparent', border: '1px solid #e2e6ea',
  borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600,
  color: '#8a94a3', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap'
};

const halfGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' };

const legendStyle = { display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' };
const legendItemStyle = { display: 'flex', alignItems: 'flex-start', gap: '8px', maxWidth: '300px' };
const legendTextStyle = { fontSize: '13px', color: '#5a6577', lineHeight: 1.4 };

const emptyStyle = { padding: '32px', textAlign: 'center', color: '#8a94a3', fontSize: '13px' };

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
const thStyle = {
  textAlign: 'left', fontSize: '11px', letterSpacing: '0.6px', color: '#8a94a3',
  fontWeight: 700, padding: '8px 10px', borderBottom: '1px solid #e2e6ea', textTransform: 'uppercase'
};
const tdStyle = { padding: '7px 10px', borderBottom: '1px solid #f0f1f3', color: '#5a6577' };
const tdNumStyle = {
  padding: '7px 10px', borderBottom: '1px solid #f0f1f3', textAlign: 'right',
  fontVariantNumeric: 'tabular-nums', color: '#1a2332', fontWeight: 600
};
const swatchStyle = {
  width: '11px', height: '11px', borderRadius: '3px',
  display: 'inline-block', verticalAlign: '-1px', marginRight: '7px'
};
